import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, gbp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plane, Hotel, Palmtree, Car, Landmark, ClipboardList, Mail, LogOut, Trash2, Plus, LayoutDashboard, TrendingUp, Loader2, Ticket, FileText, Send } from "lucide-react";
import { QuoteBuilder } from "@/components/QuoteBuilder";

const RESOURCES = {
  flights: { label: "Flights", icon: Plane, fields: ["airline", "airline_code", "origin", "origin_code", "destination", "destination_code", "depart_time", "arrive_time", "duration", "stops", "price", "featured"] },
  hotels: { label: "Hotels", icon: Hotel, fields: ["name", "city", "country", "stars", "rating", "image", "price", "featured"] },
  holidays: { label: "Holidays", icon: Palmtree, fields: ["title", "destination", "country", "image", "nights", "price", "board", "category", "featured"] },
  carhire: { label: "Car Hire", icon: Car, fields: ["name", "car_type", "location", "seats", "transmission", "image", "price_per_day"] },
  umrah: { label: "Umrah", icon: Landmark, fields: ["title", "nights", "hotel_makkah", "hotel_madinah", "image", "price", "airline", "featured"] },
  vouchers: { label: "Vouchers", icon: Ticket, fields: ["code", "percent", "active", "expires_at"] },
};
const NUM = ["price", "price_per_day", "stars", "rating", "nights", "seats", "percent"];
const BOOL = ["featured", "active"];
const DATE = ["expires_at"];

const AddDialog = ({ resource, onAdded }) => {
  const cfg = RESOURCES[resource];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const payload = { ...form };
    NUM.forEach((n) => { if (payload[n] !== undefined) payload[n] = Number(payload[n]); });
    BOOL.forEach((b) => { payload[b] = payload[b] === true || payload[b] === "true"; });
    try { await api.post(`/admin/${resource}`, payload); toast.success("Added"); setOpen(false); setForm({}); onAdded(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="admin-add-btn" className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white"><Plus className="w-4 h-4 mr-1" /> Add {cfg.label}</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add {cfg.label}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {cfg.fields.map((f) => (
            <div key={f} className={BOOL.includes(f) ? "col-span-2 flex items-center gap-2" : ""}>
              <Label className="capitalize text-xs">{f.replace(/_/g, " ")}</Label>
              {BOOL.includes(f) ? (
                <input type="checkbox" checked={form[f] === undefined ? (f === "active") : !!form[f]} onChange={(e) => set(f, e.target.checked)} data-testid={`field-${f}`} />
              ) : (
                <Input data-testid={`field-${f}`} type={NUM.includes(f) ? "number" : DATE.includes(f) ? "date" : "text"} value={form[f] || ""} onChange={(e) => set(f, e.target.value)} />
              )}
            </div>
          ))}
        </div>
        <Button onClick={save} data-testid="admin-save-btn" className="rounded-full bg-[#0B3C5D] text-white mt-2">Save</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/admin/login"); }, [loading, user, nav]);

  const loadStats = useCallback(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const loadRows = useCallback((res) => { setBusy(true); api.get(`/admin/${res}`).then(({ data }) => setRows(data)).finally(() => setBusy(false)); }, []);

  useEffect(() => { if (user) loadStats(); }, [user, loadStats]);
  useEffect(() => { if (user && tab !== "overview") loadRows(tab); }, [tab, user, loadRows]);

  const del = async (res, id) => { await api.delete(`/admin/${res}/${id}`); toast.success("Deleted"); loadRows(res); loadStats(); };

  const sendDemo = async () => {
    const email = window.prompt("Send a sample confirmation/itinerary email to:", user?.email || "");
    if (!email) return;
    try { const { data } = await api.post("/admin/send-demo-email", { email }); toast.success(`Demo email sent to ${data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send demo email"); }
  };
  const sendItinerary = async (r) => {
    const email = window.prompt("Send full itinerary/e-ticket email to:", r.email || "");
    if (!email) return;
    try { const { data } = await api.post(`/admin/bookings/${r.reference}/send-itinerary`, { email }); toast.success(`Itinerary sent to ${data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send itinerary"); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0B3C5D]" /></div>;

  const navItems = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    ...Object.entries(RESOURCES).map(([k, v]) => ({ key: k, label: v.label, icon: v.icon })),
    { key: "bookings", label: "Bookings", icon: ClipboardList },
    { key: "quote-builder", label: "Quote Builder", icon: Send },
    { key: "quotes", label: "Quotes Sent", icon: FileText },
    { key: "enquiries", label: "Enquiries", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-slate-100 grid md:grid-cols-[240px_1fr]">
      <aside className="bg-[#0B3C5D] text-slate-200 p-4 md:min-h-screen">
        <div className="flex items-center gap-2 mb-8 px-2 pt-2">
          <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center"><Plane className="w-5 h-5 text-[#FF6B35]" /></span>
          <span className="font-display font-extrabold text-white">Elia Admin</span>
        </div>
        <nav className="space-y-1">
          {navItems.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.key} data-testid={`admin-nav-${n.key}`} onClick={() => setTab(n.key)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === n.key ? "bg-[#FF6B35] text-white" : "hover:bg-white/10"}`}>
                <Icon className="w-4 h-4" /> {n.label}
              </button>
            );
          })}
        </nav>
        <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/admin/login"); }} className="mt-8 flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:text-white">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      <main className="p-6 md:p-8">
        {tab === "overview" && (
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-6">Dashboard overview</h1>
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[["Revenue", gbp(stats.revenue), TrendingUp], ["Bookings", stats.bookings, ClipboardList], ["Enquiries", stats.enquiries, Mail], ["Flights", stats.flights, Plane], ["Hotels", stats.hotels, Hotel], ["Holidays", stats.holidays, Palmtree], ["Car Hire", stats.carhire, Car], ["Umrah", stats.umrah, Landmark]].map(([label, val, Icon]) => (
                  <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`stat-${label.toLowerCase().replace(" ", "-")}`}>
                    <Icon className="w-5 h-5 text-[#FF6B35]" />
                    <p className="font-display text-2xl font-bold text-slate-900 mt-3">{val}</p>
                    <p className="text-xs text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "quote-builder" && <QuoteBuilder onSent={() => loadStats()} />}

        {tab !== "overview" && tab !== "quote-builder" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display text-2xl font-bold text-slate-900 capitalize">{navItems.find((n) => n.key === tab)?.label}</h1>
              {RESOURCES[tab] && <AddDialog resource={tab} onAdded={() => { loadRows(tab); loadStats(); }} />}
              {tab === "bookings" && <Button data-testid="send-demo-email-btn" onClick={sendDemo} className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white"><Send className="w-4 h-4 mr-1" /> Send demo email</Button>}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {busy ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B3C5D]" /></div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`admin-table-${tab}`}>
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                      <tr>{headers(tab).map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}<th className="px-4 py-3"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          {headers(tab).map((h) => <td key={h} className="px-4 py-3 text-slate-700">{render(tab, h, r)}</td>)}
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {tab === "quotes" && (
                              <button data-testid={`resend-${r.id}`} onClick={async () => { const { data } = await api.post(`/admin/quotes/${r.id}/resend`); toast[data.emailed ? "success" : "error"](data.emailed ? "Quote re-sent" : "Email failed"); }} className="text-slate-400 hover:text-[#328CC1] mr-3" title="Resend email"><Send className="w-4 h-4 inline" /></button>
                            )}
                            {tab === "bookings" && (
                              <button data-testid={`send-itinerary-${r.id}`} onClick={() => sendItinerary(r)} className="text-slate-400 hover:text-[#328CC1] mr-3" title="Send itinerary / e-ticket email"><Send className="w-4 h-4 inline" /></button>
                            )}
                            <button data-testid={`delete-${r.id}`} onClick={() => del(tab, r.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-400">No records yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function headers(tab) {
  const map = {
    flights: ["airline", "origin_code", "destination_code", "stops", "price"],
    hotels: ["name", "city", "stars", "price"],
    holidays: ["title", "destination", "nights", "price"],
    carhire: ["name", "car_type", "location", "price_per_day"],
    umrah: ["title", "nights", "airline", "price"],
    vouchers: ["code", "percent", "active", "expires_at"],
    bookings: ["reference", "item_title", "lead_name", "amount", "status", "payment_status"],
    enquiries: ["name", "email", "subject", "status"],
    quotes: ["reference", "email", "sell_price", "total", "deposit", "balance", "emailed"],
  };
  return map[tab] || [];
}
function render(tab, h, r) {
  if (h === "price" || h === "price_per_day" || h === "amount" || h === "sell_price" || h === "total" || h === "deposit" || h === "balance") return gbp(r[h] || 0);
  if (h === "emailed") return <span className={`text-xs px-2 py-0.5 rounded-full ${r[h] ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{r[h] ? "Sent" : "Not sent"}</span>;
  if (h === "percent") return `${r[h]}%`;
  if (h === "expires_at") return r[h] || "No expiry";
  if (h === "active") return <span className={`text-xs px-2 py-0.5 rounded-full ${r[h] ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{r[h] ? "Active" : "Inactive"}</span>;
  if (h === "status" || h === "payment_status") {
    const paid = r[h] === "paid" || r[h] === "confirmed";
    return <span className={`text-xs px-2 py-0.5 rounded-full ${paid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{r[h]}</span>;
  }
  const v = r[h];
  return typeof v === "string" && v.length > 34 ? v.slice(0, 34) + "…" : String(v ?? "");
}
